// import React, { useState } from "react";
// import {
//   Box,
//   Card,
//   CardContent,
//   Typography,
//   TextField,
//   Button,
//   Alert,
// } from "@mui/material";
// import { useNavigate, Link } from "react-router-dom";
// import axiosInstance from "../../utils/axios";
// import { toast } from "react-toastify";

// export default function DeliveryForgotPassword() {
//   const navigate = useNavigate();
//   const [step, setStep] = useState(1); // Step 1: Enter Email | Step 2: Enter OTP & New Password
//   const [email, setEmail] = useState("");
//   const [otp, setOtp] = useState("");
//   const [newPassword, setNewPassword] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");

//   const handleSendOTP = async (e) => {
//     e.preventDefault();
//     setError("");
//     setLoading(true);
//     try {
//       const res = await axiosInstance.post("/api/delivery/forgot-password", { email });
//       toast.success(res.data.message);
//       setStep(2);
//     } catch (err) {
//       setError(err.response?.data?.message || "Failed to send OTP");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleResetPassword = async (e) => {
//     e.preventDefault();
//     setError("");
//     setLoading(true);
//     try {
//       const res = await axiosInstance.post("/api/delivery/reset-password", {
//         email,
//         otp,
//         newPassword,
//       });
//       toast.success(res.data.message);
//       navigate("/delivery/login");
//     } catch (err) {
//       setError(err.response?.data?.message || "Reset failed");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Box minHeight="100vh" display="flex" justifyContent="center" alignItems="center" bgcolor="grey.100" p={2}>
//       <Card sx={{ maxWidth: 420, width: "100%", p: 2, borderRadius: 3, boxShadow: 3 }}>
//         <CardContent>
//           <Typography variant="h5" align="center" fontWeight="bold" gutterBottom>
//             {step === 1 ? "Forgot Password" : "Reset Password"}
//           </Typography>
//           <Typography variant="body2" color="text.secondary" align="center" mb={3}>
//             {step === 1
//               ? "Enter your registered email to receive OTP"
//               : `Enter the 6-digit OTP sent to ${email}`}
//           </Typography>

//           {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

//           {step === 1 ? (
//             <form onSubmit={handleSendOTP}>
//               <TextField
//                 fullWidth
//                 label="Registered Email"
//                 type="email"
//                 required
//                 value={email}
//                 onChange={(e) => setEmail(e.target.value)}
//               />
//               <Button fullWidth type="submit" variant="contained" size="large" sx={{ mt: 3 }} disabled={loading}>
//                 {loading ? "Sending OTP..." : "Send OTP"}
//               </Button>
//             </form>
//           ) : (
//             <form onSubmit={handleResetPassword}>
//               <TextField
//                 fullWidth
//                 label="6-Digit OTP"
//                 required
//                 value={otp}
//                 onChange={(e) => setOtp(e.target.value)}
//                 sx={{ mb: 2 }}
//               />
//               <TextField
//                 fullWidth
//                 label="New Password"
//                 type="password"
//                 required
//                 value={newPassword}
//                 onChange={(e) => setNewPassword(e.target.value)}
//               />
//               <Button fullWidth type="submit" variant="contained" size="large" sx={{ mt: 3 }} disabled={loading}>
//                 {loading ? "Resetting..." : "Reset Password"}
//               </Button>
//             </form>
//           )}

//           <Box textAlign="center" mt={3}>
//             <Link to="/delivery/login" style={{ textDecoration: "none", color: "#1976d2", fontSize: 14 }}>
//               ← Back to Delivery Login
//             </Link>
//           </Box>
//         </CardContent>
//       </Card>
//     </Box>
//   );
// }

import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
  Stack,
  Chip,
} from "@mui/material";
import {
  Email,
  Lock,
  LocalShipping,
  ArrowForward,
  ArrowBack,
  Home,
  Visibility,
  VisibilityOff,
  Shield,
  MarkEmailRead,
  Key,
  Replay,
} from "@mui/icons-material";
import { useNavigate, Link } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import { toast } from "react-toastify";

// 🎨 Brand Theme
const BRAND = {
  primary: "#FF6B00",
  primaryDark: "#E85A00",
  gradient: "linear-gradient(135deg, #FF6B00 0%, #E85A00 100%)",
};

export default function DeliveryForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  // ⏱️ Resend countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setInterval(() => setResendTimer((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [resendTimer]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/delivery/forgot-password", {
        email,
      });
      toast.success(res.data.message || "OTP sent to your email");
      setStep(2);
      setResendTimer(60);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/delivery/reset-password", {
        email,
        otp,
        newPassword,
      });
      toast.success(res.data.message || "Password reset successfully");
      navigate("/delivery/login");
    } catch (err) {
      setError(err.response?.data?.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      const res = await axiosInstance.post("/api/delivery/forgot-password", {
        email,
      });
      toast.success("OTP resent to your email");
      setResendTimer(60);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: BRAND.gradient,
        p: { xs: 2, sm: 3 },
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-120px",
          left: "-120px",
          width: 320,
          height: 320,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.08)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-140px",
          right: "-100px",
          width: 380,
          height: 380,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.06)",
          pointerEvents: "none",
        },
      }}
    >
      {/* ⬅️ Back to Home Button */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 16, sm: 24 },
          left: { xs: 16, sm: 24 },
          zIndex: 10,
        }}
      >
        <Button
          component={Link}
          to="/"
          startIcon={<ArrowBack size={18} />}
          sx={{
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.15)",
            backdropFilter: "blur(10px)",
            borderRadius: "50px",
            px: 2.5,
            py: 1,
            textTransform: "none",
            fontWeight: 600,
            fontSize: { xs: "0.8rem", sm: "0.9rem" },
            border: "1px solid rgba(255,255,255,0.2)",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.25)",
              transform: "scale(1.05)",
            },
            transition: "all 0.3s ease",
          }}
        >
          <Home sx={{ mr: 0.5, fontSize: 20 }} />
          Back to Home
        </Button>
      </Box>

      {/* Main Card */}
      <Card
        elevation={0}
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 440,
          width: "100%",
          borderRadius: 4,
          bgcolor: "#fff",
          overflow: "visible",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          mt: { xs: 8, sm: 6 },
        }}
      >
        {/* Floating Avatar */}
        <Box
          sx={{
            position: "absolute",
            top: -44,
            left: "50%",
            transform: "translateX(-50%)",
            width: 88,
            height: 88,
            borderRadius: "50%",
            background: BRAND.gradient,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 12px 28px rgba(255, 107, 0, 0.45)",
            border: "4px solid #fff",
          }}
        >
          {step === 1 ? (
            <LocalShipping sx={{ fontSize: 42, color: "#fff" }} />
          ) : (
            <Key sx={{ fontSize: 42, color: "#fff" }} />
          )}
        </Box>

        <Box sx={{ p: { xs: 3, sm: 4 }, pt: { xs: 7, sm: 7.5 } }}>
          {/* Step indicator */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            mb={2}
          >
            <Chip
              label="1"
              size="small"
              sx={{
                width: 26,
                height: 26,
                bgcolor: step >= 1 ? BRAND.primary : "#e2e8f0",
                color: "#fff",
                fontWeight: 700,
                fontSize: 12,
              }}
            />
            <Box
              sx={{
                width: 32,
                height: 2,
                bgcolor: step >= 2 ? BRAND.primary : "#e2e8f0",
                transition: "background 0.3s",
              }}
            />
            <Chip
              label="2"
              size="small"
              sx={{
                width: 26,
                height: 26,
                bgcolor: step >= 2 ? BRAND.primary : "#e2e8f0",
                color: step >= 2 ? "#fff" : "#94a3b8",
                fontWeight: 700,
                fontSize: 12,
              }}
            />
          </Box>

          {/* Heading */}
          <Typography
            variant="h5"
            align="center"
            fontWeight={800}
            sx={{ letterSpacing: -0.5, color: "#0f172a" }}
          >
            {step === 1 ? "Forgot Password?" : "Reset Password"}
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{ color: "#64748b", mt: 0.75, mb: 3, fontSize: 13 }}
          >
            {step === 1
              ? "Enter your registered email — we'll send you an OTP"
              : `Enter the 6-digit code sent to ${email}`}
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: 2,
                fontSize: 13,
                "& .MuiAlert-icon": { color: "#dc2626" },
              }}
            >
              {error}
            </Alert>
          )}

          {/* ─── STEP 1: EMAIL ─── */}
          {step === 1 && (
            <form onSubmit={handleSendOTP}>
              <Stack spacing={2.2}>
                <TextField
                  fullWidth
                  label="Registered Email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: BRAND.primary, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={textFieldSx}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  endIcon={
                    !loading && (
                      <ArrowForward sx={{ transition: "transform 0.2s" }} />
                    )
                  }
                  sx={primaryButtonSx}
                >
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                      Sending OTP...
                    </Box>
                  ) : (
                    "Send OTP"
                  )}
                </Button>
              </Stack>
            </form>
          )}

          {/* ─── STEP 2: OTP + NEW PASSWORD ─── */}
          {step === 2 && (
            <form onSubmit={handleResetPassword}>
              <Stack spacing={2.2}>
                {/* Email chip (locked) */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    bgcolor: "#f8fafc",
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <MarkEmailRead
                    sx={{ fontSize: 18, color: "#10b981", flexShrink: 0 }}
                  />
                  <Box flex={1} minWidth={0}>
                    <Typography
                      variant="caption"
                      sx={{ color: "#64748b", fontSize: 10, display: "block" }}
                    >
                      OTP sent to
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        fontSize: 13,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {email}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => {
                      setStep(1);
                      setOtp("");
                      setNewPassword("");
                      setError("");
                    }}
                    sx={{
                      textTransform: "none",
                      color: BRAND.primary,
                      fontWeight: 700,
                      fontSize: 11,
                      minWidth: "auto",
                      p: 0.5,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Change
                  </Button>
                </Box>

                {/* OTP */}
                <TextField
                  fullWidth
                  label="6-Digit OTP"
                  placeholder="● ● ● ● ● ●"
                  value={otp}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setOtp(digits);
                  }}
                  required
                  size="medium"
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 6,
                    style: {
                      letterSpacing: "0.5em",
                      fontSize: 18,
                      fontWeight: 700,
                      textAlign: "center",
                      fontFamily: "monospace",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Key sx={{ color: BRAND.primary, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={textFieldSx}
                  helperText={
                    otp.length > 0 && otp.length < 6
                      ? `${6 - otp.length} digit${6 - otp.length > 1 ? "s" : ""} remaining`
                      : ""
                  }
                />

                {/* New Password */}
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Min. 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  size="medium"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: BRAND.primary, fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          onClick={() => setShowPassword((p) => !p)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                          sx={{ color: "#94a3b8" }}
                        >
                          {showPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={textFieldSx}
                />

                {/* Reset Button */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading || otp.length !== 6 || newPassword.length < 6}
                  endIcon={
                    !loading && (
                      <ArrowForward sx={{ transition: "transform 0.2s" }} />
                    )
                  }
                  sx={primaryButtonSx}
                >
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                      Resetting...
                    </Box>
                  ) : (
                    "Reset Password"
                  )}
                </Button>

                {/* Resend OTP */}
                <Box textAlign="center">
                  {resendTimer > 0 ? (
                    <Typography
                      variant="caption"
                      sx={{ color: "#94a3b8", fontSize: 12 }}
                    >
                      Didn't receive?{" "}
                      <strong style={{ color: "#64748b" }}>
                        Resend in {resendTimer}s
                      </strong>
                    </Typography>
                  ) : (
                    <Button
                      size="small"
                      startIcon={<Replay sx={{ fontSize: 16 }} />}
                      onClick={handleResend}
                      disabled={loading}
                      sx={{
                        textTransform: "none",
                        color: BRAND.primary,
                        fontWeight: 700,
                        fontSize: 12,
                        "&:hover": { backgroundColor: "rgba(255,107,0,0.08)" },
                      }}
                    >
                      Resend OTP
                    </Button>
                  )}
                </Box>
              </Stack>
            </form>
          )}

          {/* Divider */}
          <Divider
            sx={{ my: 3, "&::before, &::after": { borderColor: "#f1f5f9" } }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#94a3b8", px: 1, fontSize: 11, fontWeight: 600 }}
            >
              SECURE RESET
            </Typography>
          </Divider>

          {/* Back to Login */}
          <Box textAlign="center">
            <Button
              component={Link}
              to="/delivery/login"
              startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none",
                color: BRAND.primaryDark,
                fontWeight: 700,
                fontSize: 13,
                "&:hover": {
                  backgroundColor: "rgba(255,107,0,0.08)",
                  textDecoration: "underline",
                },
              }}
            >
              Back to Delivery Login
            </Button>
          </Box>

          {/* Security footer */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={0.75}
            mt={2}
          >
            <Shield sx={{ fontSize: 14, color: "#10b981" }} />
            <Typography
              variant="caption"
              sx={{ color: "#64748b", fontSize: 10 }}
            >
              OTP expires in 10 minutes for your security
            </Typography>
          </Box>
        </Box>
      </Card>
 
      {/* Footer */}
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          bottom: 16,
          left: 0,
          right: 0,
          textAlign: "center",
          color: "rgba(255,255,255,0.75)",
          fontSize: 11,
          fontWeight: 500,
          zIndex: 1,
        }}
      >
        © {new Date().getFullYear()} Delivery Partner Portal
      </Typography>
    </Box>
  );
}

// 🎨 Reusable styles
const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    backgroundColor: "#f8fafc",
    transition: "all 0.2s",
    "&:hover": { backgroundColor: "#fff" },
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      boxShadow: "0 0 0 3px rgba(255, 107, 0, 0.1)",
    },
    "&.Mui-focused fieldset": {
      borderColor: BRAND.primary,
      borderWidth: 1.5,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: BRAND.primaryDark },
  "& .MuiInputLabel-root": { fontSize: 14 },
  "& .MuiOutlinedInput-input": { fontSize: 14 },
};

const primaryButtonSx = {
  mt: 1,
  py: 1.4,
  borderRadius: 2.5,
  textTransform: "none",
  fontWeight: 700,
  fontSize: 15,
  background: BRAND.gradient,
  boxShadow: "0 8px 20px rgba(255, 107, 0, 0.35)",
  transition: "all 0.2s",
  "&:hover": {
    background: `linear-gradient(135deg, ${BRAND.primaryDark} 0%, ${BRAND.primary} 100%)`,
    boxShadow: "0 10px 26px rgba(255, 107, 0, 0.45)",
    transform: "translateY(-1px)",
    "& .MuiButton-endIcon svg": { transform: "translateX(3px)" },
  },
  "&:disabled": {
    background: "#e2e8f0",
    color: "#94a3b8",
    boxShadow: "none",
  },
};